/**
 * Block Type Architecture
 *
 * This is the main entry point for all block types.
 * Import from '@/types/blocks' for a single source of truth.
 *
 * @example
 * import { PlayBlock, isPlayBlock, PlayBlockType } from '@/types/blocks'
 */

// ========== Enums ==========
export {
	type PlayBlockType,
	type HashPosition,
	type TargetingMode,
	type BallPosition,
	type PlayDirection,
	type ControlPointType,
	type BezierHandleType,
	type PathSegmentType,
	type LineStyle,
	type LineEnd,
	type PathMode,
	type PreSnapMotionType,
	type DocumentType,
	type TextAlignment,
	type DividerStyle,
	type ReferenceType,
	ROOT_BLOCK_TYPES,
	CANVAS_ROOT_TYPES,
	DOCUMENT_ROOT_TYPES,
	CANVAS_CONTENT_TYPES,
	DOCUMENT_CONTENT_TYPES,
	LAYOUT_CONTAINER_TYPES,
	REFERENCE_BLOCK_TYPES,
	isRootBlockType,
	isCanvasRootType,
	isDocumentRootType,
	isCanvasContentType,
	isDocumentContentType,
	isLayoutContainerType,
	isReferenceBlockType,
} from './Enums'

// ========== Shared Mixins ==========
export {
	type SpatialProperties,
	type CoordinateSystem,
	DEFAULT_SPATIAL,
	DEFAULT_CANVAS_SPATIAL,
	type LayoutContainerProperties,
	type LayoutProperties,
	type LayoutMode,
	type LayoutAlign,
	DEFAULT_LAYOUT,
	DEFAULT_LAYOUT_PROPERTIES,
	DEFAULT_DOCUMENT_LAYOUT,
	DEFAULT_DOCUMENT_LAYOUT_PROPERTIES,
	DEFAULT_SLIDE_LAYOUT,
	DEFAULT_SLIDE_LAYOUT_PROPERTIES,
	DEFAULT_CANVAS_LAYOUT,
	DEFAULT_CANVAS_LAYOUT_PROPERTIES,
	DEFAULT_ROW_LAYOUT_PROPERTIES,
	DEFAULT_COLUMN_LAYOUT_PROPERTIES,
	// OrganizationProperties and DEFAULT_ORGANIZATION removed - see junction tables
} from './shared'

// ========== Base Interfaces ==========
export {
	type BlockBase,
	type RootBlockBase,
	type ContainerBlockBase,
	type RootContainerBlockBase,
	type BlockReference,
	type PathConnectionDisplayOptions,
	MAX_NESTING_DEPTH,
} from './Base'

// ========== Union Types ==========
export {
	type PlayBlock_Union,
	type RootBlock,
	type CanvasRootBlock,
	type DocumentRootBlock,
	type CanvasContentBlock,
	type DocumentContentBlock,
	type LayoutContainer,
	type ReferenceBlock,
	isRootBlock,
	isCanvasRootBlock,
	isDocumentRootBlock,
	isCanvasContentBlock,
	isDocumentContentBlock,
	isLayoutContainer,
	isReferenceBlock,
} from './Union'

// ========== Canvas Blocks ==========
export {
	type PlayBlock,
	isPlayBlock,
	type FormationBlock,
	isFormationBlock,
	type ConceptBlock,
	isConceptBlock,
	type PlayerBlock,
	isPlayerBlock,
	type DrawingBlock,
	isDrawingBlock,
	type ControlPointBlock,
	isControlPointBlock,
	type EmbeddedHandles,
	type AnnotationBlock,
	isAnnotationBlock,
	type PreSnapMarkerBlock,
	isPreSnapMarkerBlock,
} from './canvas'

// ========== Document Blocks ==========
export {
	type DocumentBlock,
	isDocumentBlock,
	type DocumentTemplateType,
	type DocumentExportPageSize,
	type DocumentOrientation,
	type CreateDocumentSectionRequest,
	type SectionBlock,
	isSectionBlock,
	type TextBlock,
	isTextBlock,
	type HeaderBlock,
	isHeaderBlock,
	type DividerBlock,
	isDividerBlock,
	type ShapeBlock,
	isShapeBlock,
	type ImageBlock,
	isImageBlock,
} from './document'

// ========== Reference Blocks ==========
export {
	type ConceptReferenceBlock,
	isConceptReferenceBlock,
	type PlayReferenceBlock,
	isPlayReferenceBlock,
	type FormationReferenceBlock,
	isFormationReferenceBlock,
	type DocumentReferenceBlock,
	isDocumentReferenceBlock,
} from './reference'

// ========== Layout Blocks ==========
export {
	type RowBlock,
	isRowBlock,
	type ColumnBlock,
	isColumnBlock,
} from './layout'

// ========== DAO Types (Database Access) ==========
export {
	type BlockRow,
	type BlockRowWithOrganization,
	type BlockRowWithDepth,
	type InsertBlockData,
	type UpdateBlockData,
	type BlockReferenceRow,
	type CreateReferenceData,
	type UpdateReferenceData,
	type CreateBlockInput,
	type CreateRootBlockInput,
	type CreateNestedBlockInput,
	type BlockCountResult,
	type BlockExistsResult,
	type BlockPaginationMeta,
	type PaginatedBlockResult,
	type RootBlockDTO,
} from './DaoTypes'

// ========== Resolved Types (for Rendering) ==========
export {
	type ResolvedBlock,
	type ResolvedPlay,
	type ResolvedConceptReference,
	type ResolvedDrawing,
	type ResolvedDocument,
	type ResolvedDocumentChild,
	type ResolvedDocumentSection,
	type ResolvedDocumentText,
	type ResolvedDocumentHeader,
	type ResolvedDocumentDivider,
	type ResolvedDocumentShape,
	type ResolvedDocumentImage,
	type ResolvedDocumentPlayReference,
	type ResolvedDocumentDocumentReference,
	type ResolvedSectionChild,
	isResolvedSection,
	isResolvedPlayReference,
	isResolvedDocumentReference,
} from './ResolvedTypes'
